import React, { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import API_URL from '../../config'
import { setAuthToken } from '../../services/auth'

const RecordDetailView = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [record, setRecord] = useState(null)
  const [history, setHistory] = useState([])
  const [relatedRecords, setRelatedRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchRecordDetails = async () => {
      try {
        setLoading(true)

        // Set auth token for the request
        const token = localStorage.getItem('token')
        if (!token) {
          setError('Authentication token not found. Please log in again.')
          setLoading(false)
          return
        }

        setAuthToken(token)

        // Fetch record details
        const response = await axios.get(`${API_URL}admin/reconciliation/record/${id}/`)

        setRecord(response.data.record)
        setHistory(response.data.history)
        setRelatedRecords(response.data.relatedRecords)
        setError(null)
      } catch (err) {
        console.error('Error fetching record details:', err)
        setError('Failed to load record details. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchRecordDetails()
  }, [id])

  // Function to render status icon
  const renderStatusIcon = (status) => {
    if (status === 'active' || status === 'complete') {
      return <span className='text-green-500'>✓</span>
    } else if (status === 'pending' || status === 'processing') {
      return <span className='text-yellow-500'>⟳</span>
    } else {
      return <span className='text-red-500'>✗</span>
    }
  }

  // Format date for display
  const formatDate = (isoDate) => {
    if (!isoDate) return ''
    const date = new Date(isoDate)
    return date.toLocaleString()
  }

  if (loading) {
    return <div className='p-4'>Loading record details...</div>
  }

  if (error) {
    return <div className='text-red-500 p-4'>{error}</div>
  }

  if (!record) {
    return <div className='p-4'>Record not found</div>
  }

  return (
    <div className='p-4'>
      <h1 className='mb-6 text-2xl font-bold'>Record Detail</h1>

      {/* Navigation */}
      <div className='mb-6 flex border-b pb-2'>
        <Link to='/admin/reconciliation' className='px-4 py-2'>
          Dashboard
        </Link>
        <Link to='/admin/reconciliation/recent' className='px-4 py-2'>
          Recent
        </Link>
        <Link to='/admin/reconciliation/search' className='px-4 py-2'>
          Search
        </Link>
        <Link to='/admin/reconciliation/manual' className='px-4 py-2'>
          Manual
        </Link>
      </div>

      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className='text-blue-500 mb-4 flex items-center hover:underline'
      >
        &lt; Back
      </button>

      {/* Record Information */}
      <div className='mb-6'>
        <h2 className='mb-3 text-lg font-semibold'>RECORD INFORMATION</h2>
        <div className='grid grid-cols-1 gap-4 rounded border p-4 md:grid-cols-2'>
          <div>
            <p>
              <span className='font-semibold'>Media Type:</span>{' '}
              {record.mediaType
                ? record.mediaType.charAt(0).toUpperCase() + record.mediaType.slice(1)
                : 'Unknown'}
            </p>
            <p>
              <span className='font-semibold'>Title:</span> {record.title}
            </p>
            <p>
              <span className='font-semibold'>Artists:</span>{' '}
              {record.artists && record.artists.join(', ')}
            </p>
            <p>
              <span className='font-semibold'>Service:</span> {record.service || 'Unknown'}
            </p>
            <p>
              <span className='font-semibold'>Duration:</span>{' '}
              {record.duration
                ? `${Math.floor(record.duration / 60000)}:${((record.duration % 60000) / 1000).toFixed(0).padStart(2, '0')}`
                : 'Unknown'}
            </p>
          </div>
          <div>
            <p>
              <span className='font-semibold'>Status:</span> {renderStatusIcon(record.status)}{' '}
              {record.status}
            </p>
            <p>
              <span className='font-semibold'>Cloud ID:</span> {record.cloudId || 'None'}
            </p>
            <p>
              <span className='font-semibold'>Pending ID:</span> {record.pendingId || 'None'}
            </p>
            <p>
              <span className='font-semibold'>Processing Time:</span>{' '}
              {formatDate(record.processingTime)}
            </p>
            <p>
              <span className='font-semibold'>Match Type:</span> {record.matchType || 'Unknown'}
            </p>
          </div>
        </div>
      </div>

      {/* Reconciliation History */}
      <div className='mb-6'>
        <h2 className='mb-3 text-lg font-semibold'>RECONCILIATION HISTORY</h2>
        <div className='overflow-x-auto'>
          <table className='min-w-full border'>
            <thead>
              <tr className='bg-gray-100'>
                <th className='border px-4 py-2'>Time</th>
                <th className='border px-4 py-2'>Action</th>
                <th className='border px-4 py-2'>Details</th>
              </tr>
            </thead>
            <tbody>
              {history.length > 0 ? (
                history.map((entry, index) => (
                  <tr key={index} className='hover:bg-gray-50'>
                    <td className='border px-4 py-2'>{formatDate(entry.timestamp)}</td>
                    <td className='border px-4 py-2'>{entry.action}</td>
                    <td className='border px-4 py-2'>{entry.message}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan='3' className='border px-4 py-2 text-center'>
                    No history available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Related Records */}
      <div className='mb-6'>
        <h2 className='mb-3 text-lg font-semibold'>RELATED RECORDS</h2>
        <div className='overflow-x-auto'>
          <table className='min-w-full border'>
            <thead>
              <tr className='bg-gray-100'>
                <th className='border px-4 py-2'>Type</th>
                <th className='border px-4 py-2'>Title</th>
                <th className='border px-4 py-2'>Artists</th>
                <th className='border px-4 py-2'>Service</th>
                <th className='border px-4 py-2'>Relationship</th>
                <th className='border px-4 py-2'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {relatedRecords.length > 0 ? (
                relatedRecords.map((related, index) => (
                  <tr key={index} className='hover:bg-gray-50'>
                    <td className='border px-4 py-2 capitalize'>
                      {related.mediaType || 'Unknown'}
                    </td>
                    <td className='border px-4 py-2'>{related.title}</td>
                    <td className='border px-4 py-2'>
                      {related.artists && related.artists.join(', ')}
                    </td>
                    <td className='border px-4 py-2'>{related.service || 'Unknown'}</td>
                    <td className='border px-4 py-2 capitalize'>
                      {related.relationship.replace('_', ' ')}
                    </td>
                    <td className='border px-4 py-2'>
                      <Link
                        to={`/admin/reconciliation/record/${related.id}`}
                        className='bg-blue-500 text-white hover:bg-blue-600 rounded px-2 py-1'
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan='6' className='border px-4 py-2 text-center'>
                    No related records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className='flex gap-2'>
        <Link
          to={`/admin/reconciliation/manual?sourceId=${record.id}`}
          className='bg-blue-500 text-white hover:bg-blue-600 rounded px-4 py-2'
        >
          Manual Reconciliation
        </Link>

        {record.cloudId && (
          <button
            className='bg-red-500 text-white hover:bg-red-600 rounded px-4 py-2'
            onClick={() => alert('This functionality is not yet implemented')}
          >
            Remove From Cloud
          </button>
        )}

        <button
          className='bg-gray-200 hover:bg-gray-300 rounded px-4 py-2'
          onClick={() => alert(`Raw JSON: ${JSON.stringify(record, null, 2)}`)}
        >
          View JSON
        </button>
      </div>
    </div>
  )
}

export default RecordDetailView
